import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Text,
  Badge,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Progress,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  Tooltip,
  Select,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  FiRefreshCw,
  FiSearch,
  FiServer,
  FiChevronLeft,
  FiChevronRight,
  FiDatabase,
  FiHardDrive,
  FiActivity,
  FiCpu,
  FiLayers,
  FiTerminal,
  FiDownload,
} from 'react-icons/fi';

const API_BASE = '/api/v1/admin-ui';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatNumber(n) {
  if (n === undefined || n === null) return '0';
  return n.toLocaleString();
}

function ElasticStatus() {
  const toast = useToast();
  const [esData, setEsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Index browser state
  const [indexSearch, setIndexSearch] = useState('');
  const [indexSort, setIndexSort] = useState('size');
  const [indexList, setIndexList] = useState([]);
  const [indexPagination, setIndexPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [indicesLoading, setIndicesLoading] = useState(false);

  // Query state
  const [esPath, setEsPath] = useState('/_cat/indices?v&s=store.size:desc');
  const [esMethod, setEsMethod] = useState('GET');
  const [esBody, setEsBody] = useState('');
  const [esQueryResult, setEsQueryResult] = useState(null);
  const [esQueryRunning, setEsQueryRunning] = useState(false);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    'X-Tenant-ID': localStorage.getItem('instance_id'),
  });

  const fetchStatus = useCallback(async (showToast = false) => {
    if (showToast) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/elasticsearch/status/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setEsData(data.elasticsearch);
        if (showToast) {
          toast({ title: 'Elasticsearch status refreshed', status: 'success', duration: 2000 });
        }
      } else {
        toast({ title: 'Error loading Elasticsearch status', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error connecting to API', status: 'error', duration: 3000 });
    }

    setLoading(false);
    setRefreshing(false);
  }, [toast]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchStatus(), 15000);
    return () => clearInterval(interval);
  }, [fetchStatus, autoRefresh]);

  const fetchIndices = async (page = 1, sort = indexSort, search = indexSearch) => {
    setIndicesLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), sort, search });
      const res = await fetch(`${API_BASE}/elasticsearch/indices/?${params}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setIndexList(data.indices);
        setIndexPagination({ page: data.page, total: data.total, total_pages: data.total_pages });
      }
    } catch (e) {
      toast({ title: 'Error loading indices', status: 'error', duration: 3000 });
    }
    setIndicesLoading(false);
  };

  const executeEsQuery = async () => {
    if (!esPath.trim()) return;
    setEsQueryRunning(true);
    setEsQueryResult(null);
    try {
      const payload = { path: esPath, method: esMethod };
      if (esBody.trim() && (esMethod === 'POST' || esMethod === 'PUT')) {
        payload.body = esBody;
      }
      const res = await fetch(`${API_BASE}/elasticsearch/query/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setEsQueryResult(data);
      if (!data.success) {
        toast({ title: data.message || 'Query failed', status: 'error', duration: 5000 });
      }
    } catch (e) {
      toast({ title: 'Error executing query', status: 'error', duration: 3000 });
    }
    setEsQueryRunning(false);
  };

  const downloadJson = () => {
    if (!esQueryResult?.response) return;
    const json = JSON.stringify(esQueryResult.response, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `es_query_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="yellow.500" />
        <Text mt={4}>Loading Elasticsearch status...</Text>
      </Box>
    );
  }

  const connected = esData?.connected;
  const cluster = esData?.cluster || {};
  const nodes = esData?.nodes || {};
  const indices = esData?.indices || {};

  const clusterStatusColor = {
    green: 'green',
    yellow: 'yellow',
    red: 'red',
  };

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <HStack spacing={3}>
          <Heading size="lg">Elasticsearch Status</Heading>
          <Badge
            colorScheme={connected ? 'green' : 'red'}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {connected ? 'Connected' : 'Disconnected'}
          </Badge>
          {connected && cluster.status && (
            <Badge
              colorScheme={clusterStatusColor[cluster.status] || 'gray'}
              fontSize="sm"
              px={3}
              py={1}
              borderRadius="full"
            >
              Cluster: {cluster.status.toUpperCase()}
            </Badge>
          )}
        </HStack>
        <HStack spacing={3}>
          <Button
            size="sm"
            variant={autoRefresh ? 'solid' : 'outline'}
            colorScheme="yellow"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="blue"
            variant="outline"
            onClick={() => fetchStatus(true)}
            isLoading={refreshing}
            size="sm"
          >
            Refresh
          </Button>
        </HStack>
      </HStack>

      {esData?.error && !connected && (
        <Alert status="error" mb={6} borderRadius="lg">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Connection Error</Text>
            <Code colorScheme="red" fontSize="sm">{esData.error}</Code>
          </Box>
        </Alert>
      )}

      {connected && (
        <Tabs colorScheme="yellow" variant="enclosed">
          <TabList>
            <Tab><HStack spacing={2}><FiActivity /><Text>Overview</Text></HStack></Tab>
            <Tab onClick={() => { if (indexList.length === 0) fetchIndices(); }}>
              <HStack spacing={2}><FiLayers /><Text>Indices</Text></HStack>
            </Tab>
            <Tab><HStack spacing={2}><FiCpu /><Text>Nodes</Text></HStack></Tab>
            <Tab><HStack spacing={2}><FiTerminal /><Text>Query</Text></HStack></Tab>
          </TabList>

          <TabPanels>
            {/* ── Overview Tab ── */}
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                {/* Cluster Info */}
                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.100">
                  <HStack spacing={2} mb={4}>
                    <FiServer />
                    <Heading size="md">Cluster Info</Heading>
                  </HStack>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Stat>
                      <StatLabel>Cluster Name</StatLabel>
                      <StatNumber fontSize="lg">{cluster.name || '-'}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Latency</StatLabel>
                      <StatNumber fontSize="lg">{cluster.latency_ms} ms</StatNumber>
                      <StatHelpText>Ping round-trip</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Nodes</StatLabel>
                      <StatNumber fontSize="lg">{cluster.number_of_nodes}</StatNumber>
                      <StatHelpText>{cluster.number_of_data_nodes} data nodes</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Status</StatLabel>
                      <StatNumber fontSize="lg">
                        <Badge colorScheme={clusterStatusColor[cluster.status] || 'gray'} fontSize="md" px={3} py={1}>
                          {cluster.status?.toUpperCase()}
                        </Badge>
                      </StatNumber>
                    </Stat>
                  </SimpleGrid>
                </Box>

                {/* Shards */}
                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.100">
                  <HStack spacing={2} mb={4}>
                    <FiDatabase />
                    <Heading size="md">Shards</Heading>
                  </HStack>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Stat>
                      <StatLabel>Active Primary</StatLabel>
                      <StatNumber fontSize="lg">{cluster.active_primary_shards}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Active Total</StatLabel>
                      <StatNumber fontSize="lg">{cluster.active_shards}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Relocating</StatLabel>
                      <StatNumber fontSize="lg" color={cluster.relocating_shards > 0 ? 'orange.500' : 'inherit'}>
                        {cluster.relocating_shards}
                      </StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Unassigned</StatLabel>
                      <StatNumber fontSize="lg" color={cluster.unassigned_shards > 0 ? 'red.500' : 'inherit'}>
                        {cluster.unassigned_shards}
                      </StatNumber>
                    </Stat>
                  </SimpleGrid>
                </Box>

                {/* Index Summary */}
                <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.100">
                  <HStack spacing={2} mb={4}>
                    <FiLayers />
                    <Heading size="md">Index Summary</Heading>
                  </HStack>
                  <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                    <Stat>
                      <StatLabel>Total Indices</StatLabel>
                      <StatNumber fontSize="lg">{indices.total || 0}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Total Documents</StatLabel>
                      <StatNumber fontSize="lg">{formatNumber(indices.total_docs)}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Total Size</StatLabel>
                      <StatNumber fontSize="lg">{formatBytes(indices.total_size_bytes)}</StatNumber>
                    </Stat>
                  </SimpleGrid>
                </Box>

                {/* Node Summary Cards */}
                {nodes.list && nodes.list.length > 0 && (
                  <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.100">
                    <HStack spacing={2} mb={4}>
                      <FiCpu />
                      <Heading size="md">Nodes ({nodes.total})</Heading>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      {nodes.list.map((node, idx) => (
                        <Box key={idx} p={4} borderRadius="md" border="1px" borderColor="gray.200" bg="gray.50">
                          <HStack justify="space-between" mb={3}>
                            <Text fontWeight="bold">{node.name}</Text>
                            <Badge colorScheme="blue" fontSize="xs">v{node.version}</Badge>
                          </HStack>
                          <VStack align="stretch" spacing={2} fontSize="sm">
                            <Box>
                              <Text color="gray.600" mb={1}>Roles</Text>
                              <Wrap spacing={1}>
                                {node.roles?.map((r, i) => (
                                  <WrapItem key={i}>
                                    <Badge size="sm" colorScheme="gray" fontSize="xs">{r}</Badge>
                                  </WrapItem>
                                ))}
                              </Wrap>
                            </Box>
                            <Box>
                              <HStack justify="space-between" mb={1}>
                                <Text color="gray.600">JVM Heap</Text>
                                <Text>{node.jvm_heap_percent}%</Text>
                              </HStack>
                              <Progress
                                value={node.jvm_heap_percent}
                                size="sm"
                                colorScheme={node.jvm_heap_percent > 85 ? 'red' : node.jvm_heap_percent > 70 ? 'yellow' : 'green'}
                                borderRadius="full"
                              />
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                {formatBytes(node.jvm_heap_used)} / {formatBytes(node.jvm_heap_max)}
                              </Text>
                            </Box>
                            <Box>
                              <HStack justify="space-between" mb={1}>
                                <Text color="gray.600">Memory</Text>
                                <Text>{node.os_mem_percent}%</Text>
                              </HStack>
                              <Progress
                                value={node.os_mem_percent}
                                size="sm"
                                colorScheme={node.os_mem_percent > 90 ? 'red' : node.os_mem_percent > 75 ? 'yellow' : 'green'}
                                borderRadius="full"
                              />
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                {formatBytes(node.os_mem_used)} / {formatBytes(node.os_mem_total)}
                              </Text>
                            </Box>
                            <Box>
                              <HStack justify="space-between" mb={1}>
                                <Text color="gray.600">Disk</Text>
                                <Text>{node.disk_total > 0 ? Math.round((1 - node.disk_available / node.disk_total) * 100) : 0}%</Text>
                              </HStack>
                              <Progress
                                value={node.disk_total > 0 ? (1 - node.disk_available / node.disk_total) * 100 : 0}
                                size="sm"
                                colorScheme={
                                  node.disk_total > 0 && (1 - node.disk_available / node.disk_total) > 0.9
                                    ? 'red'
                                    : node.disk_total > 0 && (1 - node.disk_available / node.disk_total) > 0.75
                                    ? 'yellow'
                                    : 'green'
                                }
                                borderRadius="full"
                              />
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                {formatBytes(node.disk_available)} free / {formatBytes(node.disk_total)} total
                              </Text>
                            </Box>
                          </VStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </VStack>
            </TabPanel>

            {/* ── Indices Tab ── */}
            <TabPanel px={0}>
              <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.100">
                <HStack spacing={2} mb={4}>
                  <FiLayers />
                  <Heading size="md">Indices</Heading>
                </HStack>

                <HStack mb={4} spacing={3}>
                  <InputGroup size="sm" maxW="300px">
                    <InputLeftElement><FiSearch color="gray" /></InputLeftElement>
                    <Input
                      placeholder="Filter indices..."
                      value={indexSearch}
                      onChange={(e) => setIndexSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') fetchIndices(1, indexSort, indexSearch); }}
                    />
                  </InputGroup>
                  <Select
                    size="sm"
                    maxW="160px"
                    value={indexSort}
                    onChange={(e) => { setIndexSort(e.target.value); fetchIndices(1, e.target.value, indexSearch); }}
                  >
                    <option value="size">Sort by Size</option>
                    <option value="docs">Sort by Docs</option>
                    <option value="name">Sort by Name</option>
                    <option value="searches">Sort by Searches</option>
                  </Select>
                  <Button size="sm" colorScheme="blue" variant="outline" onClick={() => fetchIndices(1, indexSort, indexSearch)}>
                    Search
                  </Button>
                </HStack>

                {indicesLoading ? (
                  <Box textAlign="center" py={10}><Spinner /></Box>
                ) : (
                  <>
                    <Box overflowX="auto">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>Index Name</Th>
                            <Th isNumeric>Documents</Th>
                            <Th isNumeric>Deleted</Th>
                            <Th isNumeric>Size</Th>
                            <Th isNumeric>Searches</Th>
                            <Th isNumeric>Search Time</Th>
                            <Th isNumeric>Indexing Ops</Th>
                            <Th isNumeric>Index Time</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {indexList.map((idx, i) => (
                            <Tr key={i} _hover={{ bg: 'gray.50' }}>
                              <Td>
                                <Tooltip label={idx.name}>
                                  <Code fontSize="xs" maxW="280px" isTruncated display="block">{idx.name}</Code>
                                </Tooltip>
                              </Td>
                              <Td isNumeric>{formatNumber(idx.docs_count)}</Td>
                              <Td isNumeric color={idx.docs_deleted > 0 ? 'orange.500' : 'inherit'}>{formatNumber(idx.docs_deleted)}</Td>
                              <Td isNumeric>{formatBytes(idx.size_bytes)}</Td>
                              <Td isNumeric>{formatNumber(idx.search_query_total)}</Td>
                              <Td isNumeric>{formatNumber(idx.search_query_time_ms)} ms</Td>
                              <Td isNumeric>{formatNumber(idx.indexing_total)}</Td>
                              <Td isNumeric>{formatNumber(idx.indexing_time_ms)} ms</Td>
                            </Tr>
                          ))}
                          {indexList.length === 0 && (
                            <Tr><Td colSpan={8} textAlign="center" py={8} color="gray.500">No indices found</Td></Tr>
                          )}
                        </Tbody>
                      </Table>
                    </Box>

                    {indexPagination.total_pages > 1 && (
                      <HStack justify="center" mt={4} spacing={2}>
                        <IconButton
                          icon={<FiChevronLeft />}
                          size="sm"
                          variant="outline"
                          isDisabled={indexPagination.page <= 1}
                          onClick={() => fetchIndices(indexPagination.page - 1)}
                        />
                        <Text fontSize="sm" color="gray.600">
                          Page {indexPagination.page} of {indexPagination.total_pages} ({indexPagination.total} indices)
                        </Text>
                        <IconButton
                          icon={<FiChevronRight />}
                          size="sm"
                          variant="outline"
                          isDisabled={indexPagination.page >= indexPagination.total_pages}
                          onClick={() => fetchIndices(indexPagination.page + 1)}
                        />
                      </HStack>
                    )}
                  </>
                )}
              </Box>
            </TabPanel>

            {/* ── Nodes Tab ── */}
            <TabPanel px={0}>
              <VStack spacing={4} align="stretch">
                {nodes.list && nodes.list.length > 0 ? nodes.list.map((node, idx) => (
                  <Box key={idx} bg="white" p={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.100">
                    <Box mb={4}>
                      <HStack spacing={3} mb={2}>
                        <FiCpu />
                        <Heading size="md">{node.name}</Heading>
                        <Badge colorScheme="blue">v{node.version}</Badge>
                      </HStack>
                      <Wrap spacing={1}>
                        {node.roles?.map((r, i) => (
                          <WrapItem key={i}>
                            <Badge colorScheme="purple" variant="subtle" fontSize="xs">{r}</Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                      <Box>
                        <Text fontWeight="semibold" mb={2} color="gray.700">JVM Heap</Text>
                        <Progress
                          value={node.jvm_heap_percent}
                          size="lg"
                          colorScheme={node.jvm_heap_percent > 85 ? 'red' : node.jvm_heap_percent > 70 ? 'yellow' : 'green'}
                          borderRadius="full"
                          mb={1}
                        />
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">{formatBytes(node.jvm_heap_used)}</Text>
                          <Text fontSize="sm" fontWeight="bold">{node.jvm_heap_percent}%</Text>
                          <Text fontSize="sm" color="gray.600">{formatBytes(node.jvm_heap_max)}</Text>
                        </HStack>
                      </Box>
                      <Box>
                        <Text fontWeight="semibold" mb={2} color="gray.700">OS Memory</Text>
                        <Progress
                          value={node.os_mem_percent}
                          size="lg"
                          colorScheme={node.os_mem_percent > 90 ? 'red' : node.os_mem_percent > 75 ? 'yellow' : 'green'}
                          borderRadius="full"
                          mb={1}
                        />
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">{formatBytes(node.os_mem_used)}</Text>
                          <Text fontSize="sm" fontWeight="bold">{node.os_mem_percent}%</Text>
                          <Text fontSize="sm" color="gray.600">{formatBytes(node.os_mem_total)}</Text>
                        </HStack>
                      </Box>
                      <Box>
                        <Text fontWeight="semibold" mb={2} color="gray.700">Disk</Text>
                        <Progress
                          value={node.disk_total > 0 ? (1 - node.disk_available / node.disk_total) * 100 : 0}
                          size="lg"
                          colorScheme={
                            node.disk_total > 0 && (1 - node.disk_available / node.disk_total) > 0.9
                              ? 'red'
                              : node.disk_total > 0 && (1 - node.disk_available / node.disk_total) > 0.75
                              ? 'yellow'
                              : 'green'
                          }
                          borderRadius="full"
                          mb={1}
                        />
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">{formatBytes(node.disk_total - node.disk_available)} used</Text>
                          <Text fontSize="sm" fontWeight="bold">
                            {node.disk_total > 0 ? Math.round((1 - node.disk_available / node.disk_total) * 100) : 0}%
                          </Text>
                          <Text fontSize="sm" color="gray.600">{formatBytes(node.disk_total)} total</Text>
                        </HStack>
                      </Box>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                      <HStack>
                        <Text fontSize="sm" color="gray.600">OS:</Text>
                        <Text fontSize="sm">{node.os}</Text>
                      </HStack>
                    </SimpleGrid>
                  </Box>
                )) : (
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    No node information available
                  </Alert>
                )}
              </VStack>
            </TabPanel>

            {/* Query Tab */}
            <TabPanel px={0}>
              <VStack spacing={4} align="stretch">
                <HStack spacing={4}>
                  <Box flex="1">
                    <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.400">Path</Text>
                    <Input
                      value={esPath}
                      onChange={(e) => setEsPath(e.target.value)}
                      placeholder="/_cat/indices?v"
                      bg="gray.700"
                      color="green.300"
                      fontFamily="mono"
                      fontSize="sm"
                      border="1px solid"
                      borderColor="gray.600"
                      _focus={{ borderColor: 'blue.400' }}
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          executeEsQuery();
                        }
                      }}
                    />
                  </Box>
                  <Box w="140px">
                    <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.400">Method</Text>
                    <Select
                      value={esMethod}
                      onChange={(e) => setEsMethod(e.target.value)}
                      bg="gray.700"
                      color="green.300"
                      fontFamily="mono"
                      fontSize="sm"
                      border="1px solid"
                      borderColor="gray.600"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="DELETE">DELETE</option>
                      <option value="HEAD">HEAD</option>
                    </Select>
                  </Box>
                </HStack>

                {(esMethod === 'POST' || esMethod === 'PUT') && (
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.400">Request Body (JSON)</Text>
                    <Textarea
                      value={esBody}
                      onChange={(e) => setEsBody(e.target.value)}
                      placeholder='{"query": {"match_all": {}}}'
                      bg="gray.800"
                      color="green.300"
                      fontFamily="mono"
                      fontSize="sm"
                      minH="120px"
                      border="1px solid"
                      borderColor="gray.600"
                      _focus={{ borderColor: 'blue.400' }}
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          executeEsQuery();
                        }
                      }}
                    />
                  </Box>
                )}

                <HStack>
                  <Button
                    leftIcon={<FiTerminal />}
                    colorScheme="blue"
                    size="sm"
                    onClick={executeEsQuery}
                    isLoading={esQueryRunning}
                    loadingText="Running..."
                  >
                    Execute (⌘+Enter)
                  </Button>
                  {esQueryResult && esQueryResult.success && (
                    <Button
                      leftIcon={<FiDownload />}
                      colorScheme="orange"
                      size="sm"
                      variant="outline"
                      onClick={downloadJson}
                    >
                      Download JSON
                    </Button>
                  )}
                </HStack>

                {esQueryResult && esQueryResult.error && (
                  <Alert status="error" bg="red.900" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">{esQueryResult.error}</Text>
                  </Alert>
                )}

                {esQueryResult && esQueryResult.success && (
                  <Box>
                    <HStack mb={2} spacing={4}>
                      <Badge colorScheme={esQueryResult.status_code >= 200 && esQueryResult.status_code < 300 ? 'green' : esQueryResult.status_code >= 400 ? 'red' : 'yellow'} fontSize="sm" px={2}>
                        {esQueryResult.status_code}
                      </Badge>
                      <Text fontSize="xs" color="gray.400">
                        {esQueryResult.duration_ms}ms
                      </Text>
                    </HStack>
                    <Box
                      bg="gray.800"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.600"
                      maxH="500px"
                      overflowY="auto"
                      css={{
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: '#1a202c' },
                        '&::-webkit-scrollbar-thumb': { background: '#4a5568', borderRadius: '4px' },
                      }}
                    >
                      <Code
                        display="block"
                        whiteSpace="pre"
                        p={4}
                        bg="transparent"
                        color="green.300"
                        fontFamily="mono"
                        fontSize="xs"
                        overflowX="auto"
                      >
                        {typeof esQueryResult.response === 'object'
                          ? JSON.stringify(esQueryResult.response, null, 2)
                          : esQueryResult.response}
                      </Code>
                    </Box>
                  </Box>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
}

export default ElasticStatus;
